import type { Incident, IncidentKind, IncidentStatus } from './incidents-api';

export type IncidentTone = 'ok' | 'warn' | 'down' | 'info';

const TERMINAL_STATUSES: ReadonlySet<IncidentStatus> = new Set([
  'resolved',
  'complete',
]);

export function isTerminalStatus(status: IncidentStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Severity tone for status badges/icons:
 * - resolved / complete  -> ok      (green)
 * - investigating / identified -> down (red - active outage)
 * - monitoring           -> warn    (orange - outage in recovery)
 * - scheduled / in_progress -> info (blue - maintenance)
 */
export function statusTone(status: IncidentStatus): IncidentTone {
  switch (status) {
    case 'resolved':
    case 'complete':
      return 'ok';
    case 'investigating':
    case 'identified':
      return 'down';
    case 'monitoring':
      return 'warn';
    case 'scheduled':
    case 'in_progress':
      return 'info';
    default:
      // Unknown status from unvalidated public API — fail safe to
      // needs-attention rather than returning undefined.
      return 'down';
  }
}

export function statusBadge(status: IncidentStatus): string {
  switch (status) {
    case 'investigating':
      return 'INVESTIGATING';
    case 'identified':
      return 'IDENTIFIED';
    case 'monitoring':
      return 'MONITORING';
    case 'resolved':
      return 'RESOLVED';
    case 'scheduled':
      return 'MAINTENANCE SCHEDULED';
    case 'in_progress':
      return 'IN MAINTENANCE';
    case 'complete':
      return 'MAINTENANCE COMPLETE';
    default:
      // Unknown status from unvalidated public API — show a non-empty
      // label rather than rendering a blank badge.
      return 'INCIDENT';
  }
}

export function incidentTone(incident: Incident): IncidentTone {
  if (isTerminalStatus(incident.status)) return 'ok';
  if (incident.kind === 'maintenance') return 'info';
  return incident.status === 'monitoring' ? 'warn' : 'down';
}

const INCIDENT_TONE_RANK: Record<IncidentTone, number> = {
  down: 0,
  warn: 1,
  info: 2,
  ok: 3,
};

/**
 * The most severe tone across a set of incidents (down > warn > info > ok).
 * Returns 'ok' for an empty list. Used to colour the banner when incidents
 * are active.
 */
export function worstIncidentTone(
  incidents: readonly Incident[],
): IncidentTone {
  let worst: IncidentTone = 'ok';
  for (const i of incidents) {
    const t = incidentTone(i);
    if (INCIDENT_TONE_RANK[t] < INCIDENT_TONE_RANK[worst]) worst = t;
  }
  return worst;
}

export function isMaintenance(kind: IncidentKind): boolean {
  return kind === 'maintenance';
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) {
    const m = Math.round(seconds / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  if (seconds < 86_400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
    return `${h}h ${m}m`;
  }
  const d = Math.floor(seconds / 86_400);
  return `${d} day${d === 1 ? '' : 's'}`;
}

export function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  return date.toLocaleDateString('en-US', opts);
}

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = now.getTime() - t;
  const diffSec = Math.round(diffMs / 1000);
  // Future timestamp (clock skew or a genuinely scheduled time) — don't
  // claim 'just now'. Render nothing, matching the NaN-fallback convention.
  if (diffSec < 0) return '';
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) {
    const m = Math.round(diffSec / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diffSec < 86_400) {
    const h = Math.round(diffSec / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diffSec < 86_400 * 30) {
    const d = Math.round(diffSec / 86_400);
    return `${d} day${d === 1 ? '' : 's'} ago`;
  }
  const months = Math.round(diffSec / (86_400 * 30));
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

export function monthLabel(month: string): string {
  // month = "YYYY-MM"
  const [yyyy, mm] = month.split('-');
  const year = Number.parseInt(yyyy ?? '', 10);
  const monthIdx = Number.parseInt(mm ?? '', 10) - 1;
  if (Number.isNaN(year) || Number.isNaN(monthIdx)) return month;
  const date = new Date(Date.UTC(year, monthIdx, 1));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

export function incidentHeadline(incident: Incident): string {
  if (incident.resolvedAt && incident.durationSec != null) {
    return `Resolved in ${formatDuration(incident.durationSec)} - ${formatTimestamp(incident.startedAt)}`;
  }
  return `Active - ${statusBadge(incident.status)}`;
}
